import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../../api";
import { SkeletonTableRows, Spinner } from "../../components/Loaders";
import type { AdminUserRow } from "../../types";

export function AdminUsers() {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<AdminUserRow[]>("/api/admin/users?limit=500");
      setUsers(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!query.trim()) return users;
    const q = query.trim().toLowerCase();
    return users.filter(
      (u) =>
        (u.email ?? "").toLowerCase().includes(q) ||
        (u.displayName ?? "").toLowerCase().includes(q) ||
        u.uid.toLowerCase().includes(q),
    );
  }, [users, query]);

  const onSetPlan = async (uid: string, plan: "free" | "pro" | "plus") => {
    try {
      await apiPost(`/api/admin/users/${uid}/plan`, { plan });
      setUsers((xs) => xs.map((u) => (u.uid === uid ? { ...u, plan } : u)));
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const onSetRole = async (uid: string, role: "user" | "admin") => {
    try {
      await apiPost(`/api/admin/users/${uid}/role`, { role });
      setUsers((xs) => xs.map((u) => (u.uid === uid ? { ...u, role } : u)));
    } catch (e) {
      alert((e as Error).message);
    }
  };

  return (
    <>
      <div className="admin-page-header">
        <h2>Users ({loading ? "…" : filtered.length})</h2>
        <div className="admin-page-header__tools">
          <input
            type="text"
            placeholder="Search email, name, uid"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={loading}
          />
          <button className="secondary small" onClick={load} disabled={loading}>
            {loading ? (
              <>
                <Spinner className="button-spinner" />
                Loading
              </>
            ) : (
              "Refresh"
            )}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Plan</th>
              <th>Role</th>
              <th>Today</th>
              <th>Stripe</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonTableRows rows={6} cols={6} />
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="muted" style={{ padding: 24, textAlign: "center" }}>
                  No users match that filter.
                </td>
              </tr>
            ) : (
              filtered.map((u) => (
                <tr key={u.uid}>
                  <td>
                    <div>{u.email ?? "(no email)"}</div>
                    <div className="hint">{u.displayName || u.uid}</div>
                  </td>
                  <td>
                    <span className={u.plan === "pro" || u.plan === "plus" ? "badge badge-pro" : "badge"}>
                      {(u.plan || "free").toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span className={u.role === "admin" ? "badge badge-admin" : "badge"}>
                      {(u.role || "user").toUpperCase()}
                    </span>
                  </td>
                  <td>{u.buildsToday ?? 0}</td>
                  <td className="hint">
                    {u.stripeCustomerId ? (
                      <code>{u.stripeCustomerId}</code>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="actions-cell">
                    {u.plan === "pro" || u.plan === "plus" ? (
                      <button
                        className="secondary small"
                        onClick={() => onSetPlan(u.uid, "free")}
                      >
                        Downgrade
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="primary small"
                        onClick={() => onSetPlan(u.uid, "pro")}
                      >
                        Comp Pro
                      </button>
                    )}
                    {u.plan !== "plus" && (
                      <button
                        type="button"
                        className="secondary small"
                        onClick={() => onSetPlan(u.uid, "plus")}
                      >
                        Comp Plus
                      </button>
                    )}
                    {u.role === "admin" ? (
                      <button
                        className="danger small"
                        onClick={() => onSetRole(u.uid, "user")}
                      >
                        Revoke admin
                      </button>
                    ) : (
                      <button
                        className="secondary small"
                        onClick={() => onSetRole(u.uid, "admin")}
                      >
                        Make admin
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="hint" style={{ marginTop: 14 }}>
        Users only appear here after they sign in for the first time. To
        bootstrap your first admin, run{" "}
        <code>python -m backend.admin promote &lt;your-email&gt;</code> on the
        server and then sign out/in.
      </p>
    </>
  );
}
